import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import type { SponsoredMembership } from "../payments.types";
import type { AdminBilling, MyBilling, PayInfo } from "../payments.types";

export function usePayInfo(token: string | undefined) {
  const query = useQuery<PayInfo>({
    queryKey: ["afell", "pay", token],
    queryFn: () => apiRequest(`/afell/pay/${token}/`),
    enabled: Boolean(token),
    retry: false,
  });
  // Funnel tracking: fire-and-forget page view once the token resolves.
  useEffect(() => {
    if (token && query.isSuccess) {
      apiRequest(`/afell/pay/${token}/track-view/`, "POST").catch(() => {});
    }
  }, [token, query.isSuccess]);
  return query;
}

export function useActivateFree(token: string | undefined) {
  return useMutation({
    mutationFn: () => apiRequest(`/afell/pay/${token}/activate-free/`, "POST"),
  });
}

/**
 * Stripe redirects: request the checkout URL, then hard-navigate to it.
 *
 * Two shapes, because they are genuinely different transactions. `donation`
 * is the standalone free-membership-plus-donation path with its own endpoint;
 * `membership` is the cart — N memberships, one service fee, and optionally a
 * donation, billed in the same session.
 */
export type CheckoutRequest =
  | { kind: "donation" }
  | {
      kind: "membership";
      variant?: string;
      quantity?: number;
      donation?: boolean;
    };

export function useCheckout(token: string | undefined) {
  return useMutation<{ url: string }, Error, CheckoutRequest>({
    mutationFn: (req) =>
      req.kind === "donation"
        ? apiRequest(`/afell/pay/${token}/donation-checkout/`, "POST", {})
        : apiRequest(`/afell/pay/${token}/checkout/`, "POST", {
            variant: req.variant,
            quantity: req.quantity ?? 1,
            donation: Boolean(req.donation),
          }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useMyBilling(enabled = true) {
  return useQuery<MyBilling>({
    queryKey: ["afell", "billing", "me"],
    queryFn: () => apiRequest("/afell/me/billing/"),
    enabled,
  });
}

export function useBillingPortal() {
  return useMutation<{ url: string }, Error, void>({
    mutationFn: () => apiRequest("/afell/billing/portal/", "POST"),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useAdminBilling() {
  return useQuery<AdminBilling>({
    queryKey: ["afell", "billing", "admin"],
    queryFn: () => apiRequest("/afell/admin/billing/"),
  });
}

export function useSyncSubscriptions() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, void>({
    mutationFn: () => apiRequest("/afell/admin/billing/sync/", "POST"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell", "billing"] }),
  });
}

/** Memberships this member paid for on someone else's behalf.
 *
 * Named at any time after checkout — that is the point of the feature, so the
 * list is a normal query the billing page refetches after each assignment
 * rather than something only reachable in the moments after paying.
 */
export function useMySponsorships() {
  return useQuery<{ sponsorships: SponsoredMembership[]; unassigned: number }>({
    queryKey: ["afell", "sponsorships"],
    queryFn: () => apiRequest("/afell/me/sponsorships/"),
  });
}

export function useAssignSponsorship() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: number; email: string }>({
    mutationFn: ({ id, email }) =>
      apiRequest(`/afell/me/sponsorships/${id}/assign/`, "POST", { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "sponsorships"] });
    },
  });
}

/** Take out your own membership from the billing page, optionally sponsoring
 * others in the same transaction. Replaces the emailed `/pay/<token>` link. */
export function useBuyMembership() {
  return useMutation<{ url: string }, Error, { sponsorships?: number }>({
    mutationFn: (body) =>
      apiRequest("/afell/me/membership/buy/", "POST", {
        sponsorships: body.sponsorships ?? 0,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/** Pay for memberships for other people. Open to every tier. */
export function useBuySponsorships() {
  return useMutation<{ url: string }, Error, { quantity: number }>({
    mutationFn: (body) =>
      apiRequest("/afell/me/sponsorships/buy/", "POST", body),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/** Donate. Buys no access, so nothing gates it. */
/** Stop a subscription at the end of the period it is paid up to, or resume
 *  one that was stopped. */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation<
    { cancelAtPeriodEnd: boolean },
    Error,
    { id: string; resume?: boolean }
  >({
    mutationFn: ({ id, resume }) =>
      apiRequest(`/afell/me/subscriptions/${id}/cancel/`, "POST", {
        resume: Boolean(resume),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "billing"] });
    },
  });
}

/** Change what a standing donation gives each month, where it is listed. */
export function useUpdateDonationAmount() {
  const queryClient = useQueryClient();
  return useMutation<
    { amountCents: number },
    Error,
    { id: string; amountCents: number }
  >({
    mutationFn: ({ id, amountCents }) =>
      apiRequest(`/afell/me/subscriptions/${id}/amount/`, "POST", {
        amount_cents: amountCents,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "billing"] });
    },
  });
}

/** A standing monthly contribution, at an amount the member chooses.
 *
 * The amount is sent rather than picked on Stripe's page: Stripe's
 * pay-what-you-want field only works on one-off prices, and this is a
 * subscription. */
export function useDonate() {
  return useMutation<{ url: string }, Error, number>({
    mutationFn: (amountCents) =>
      apiRequest("/afell/me/donate/", "POST", { amount_cents: amountCents }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}
